import { useLatestCallback } from '@hover/blueprint';
import type { RefObject } from 'react';
import { useEffect, useMemo, useRef } from 'react';

export interface MutationObserverOptions {
  ref: RefObject<HTMLElement>;
  options?: MutationObserverInit;
  callback: MutationCallback;
}

export const useMutationObserver = ({
  ref,
  options: optionsProp = { childList: true },
  callback: callbackProp,
}: MutationObserverOptions) => {
  const observer = useRef<MutationObserver | null>(null);
  const callback = useLatestCallback(callbackProp);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const options = useMemo(() => optionsProp, Object.values(optionsProp));

  useEffect(() => {
    const { current: target } = ref;

    if (!target || !callback) return undefined;

    if (!observer.current) {
      observer.current = new MutationObserver(callback);
    }

    observer.current.observe(target, options);

    return () => {
      observer.current?.disconnect();
      observer.current = null;
    };
  }, [callback, options, ref]);
};
